import {
  FormControlLabel,
  IconButton,
  Switch,
  makeStyles,
  Tooltip,
  TextField,
  CircularProgress,
  Typography
} from '@material-ui/core';
import { groupBy, isEqual } from 'lodash';
import { BoxWrapper, Button, Modal, Prompt } from 'components/Base';
import Fields from 'components/Fields';
import { Field, Formik, FormikProps } from 'formik';
import React, { CSSProperties, useEffect, useState } from 'react';
import InfoIcon from '@material-ui/icons/Info';
import api from 'api';
import Icons from 'components/Icons';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import { AxiosResponse } from 'axios';
import { errHelper, formatPercent, Notify, unFormatPercent } from 'utils';
import moment from 'moment';
import ModelPortfolioAllocationOptions from '../ModelPortfolioAllocationOptions';
import * as Yup from 'yup';
import Tabs from '@material-ui/core/Tabs';
import Tab from 'components/Base/Tabs/FixedTab';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CloseIcon from '@material-ui/icons/Close';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';
import { TooltipIcon } from 'components/Base';
import { useCSVReader, useCSVDownloader } from 'react-papaparse';
import Checkbox from 'components/Checkbox/Checkbox';
import { linkColor, secondaryText } from 'utils/colors';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import Warning from 'components/Charts/Utils/Warning';
import ButtonDropdown from 'components/Base/ButtonDropdown/ButtonDropdown';
import { Alert } from '@mui/material';

const modelPortfolioValidationSchema = Yup.object().shape({
  modelPortfolioName: Yup.string().required('Required')
});

const footprintValidationSchema = Yup.object().shape({
  date: Yup.string().required('Required')
});

const useStyles = makeStyles({
  progressBox: {
    position: 'absolute',
    top: '66px',
    width: '100%',
    height: 'calc(100% - 66px)',
    backgroundColor: '#fff',
    zIndex: 9,
    borderRadius: '8px'
  },
  progressSpinner: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  mpFundRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '1rem',
    padding: '0.5rem'
  },
  buttonContainer: {
    width: '100%',
    padding: '1rem',
    position: 'absolute',
    bottom: 0,
    height: '70px',
    backgroundColor: 'white',
    borderTop: '1px solid #F0F0F0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomRightRadius: '25px',
    borderBottomLeftRadius: '25px'
  },
  errorContainer: {
    height: 'max-content',
    width: '695px',
    position: 'absolute',
    bottom: '70px',
    left: '305px'
  },
  errorText: {
    fontSize: '0.8125rem',
    margin: 0,
    color: '#ec6a21',
    lineHeight: '1'
  },
  errorBox: {
    display: 'flex',
    padding: '0.5rem 1rem',
    alignItems: 'center',
    backgroundColor: '#f7f7f7'
  },
  icon: {
    fill: '#ec6a21',
    width: '1.5rem',
    marginRight: '1rem'
  },
  total: {
    fontWeight: 600,
    color: '#0080bd',
    margin: '1rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 'calc(100% - 2rem)'
  },
  titleRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '1rem',
    paddingRight: '1.5rem',
    paddingLeft: '1.5rem',
    color: '#2f2f2f80'
  },
  nameAndDate: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-betweem'
  },
  mutedText: {
    color: secondaryText,
    fontSize: '0.9em',
    marginBottom: '12px',
    '& p': {
      color: linkColor,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline',
      fontWeight: '600',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  instructions: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    border: '1px solid #e2e6ea',
    borderRadius: '4px',
    marginBottom: '16px',
    '& p': {
      margin: '0 0 8px',
      fontWeight: '600'
    },
    '& ul': {
      margin: 0,
      paddingLeft: '20px'
    },
    '& li': {
      marginBottom: '6px',
      fontSize: '0.95em'
    }
  },
  row: {
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  '@global': {
    '.MuiSvgIcon-colorSecondary': {
      color: '#0080bd !important'
    }
  }
});

interface ModelPortfolioCreatorProps {
  addModelPortfolioSelection: Function;
  close: Function;
  setHasChanges: Function;
  hasChanges: boolean;
  modelPortfolioId?: number;
  setIsModalHidden: Function;
  date?: string;
  hideDate?: boolean;
  onClone?: Function | null;
  onCreate?: Function | null;
  investments: any[];
  canHaveHistory?: boolean;
  canLock?: boolean;
  children?: any;
  defaults?: any;
}

const ModelPortfolioCreator = ({
  modelPortfolioId,
  addModelPortfolioSelection,
  close,
  setHasChanges,
  hasChanges,
  setIsModalHidden,
  onClone = null,
  onCreate = null,
  investments,
  canHaveHistory = true,
  canLock = true,
  children,
  defaults = null
}: ModelPortfolioCreatorProps) => {
  const { CSVReader } = useCSVReader();
  const { CSVDownloader } = useCSVDownloader();
  const defaultDate = moment().format('YYYY-MM-DD');
  const emptyDefinition = {
    id: null,
    date: null,
    fund_allocations: []
  } as any;

  const [tab, setTab] = useState(0);
  const [modelPortfolioName, setModelPortfolioName] = useState(
    defaults?.name || ''
  );
  const [locked, setLocked] = useState(false);
  const [lockedUser, setLockedUser] = useState(null) as any;
  const [lockedByAnotherUser, setLockedByAnotherUser] = useState(false);
  const [mpDate, setMpDate] = useState(defaultDate);
  const [editableDate, setEditableDate] = useState(defaultDate);
  const [error, setError] = useState('');
  const [funds, setFunds] = useState(defaults?.funds || []);
  const [storedDefinitions, setStoredDefinitions] = useState([] as any);
  const [definitions, setDefinitions] = useState([] as any);
  const [currentDefinition, setCurrentDefinition] = useState(emptyDefinition);
  const [definitionToEdit, setDefinitionToEdit] = useState(null as any);
  const [definitionToDelete, setDefinitionToDelete] = useState(emptyDefinition);
  const [currentValues, setCurrentValues] = useState({} as any);
  const [sumTotal, setSumTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(!!modelPortfolioId);
  const [isNewFootprintModalOpen, setIsNewFootprintModalOpen] = useState(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingFootprint, setIsAddingFootprint] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [nameToSave, setNameToSave] = useState(modelPortfolioName);
  const classes = useStyles();
  const { user } = useAuth();
  const [missingIsins, setMissingIsins] = useState([]) as any;

  const [selectedSaveOptionIndex, setselectedSaveOptionIndex] = useState(1);

  const [canClone, setCanClone] = useState(!!modelPortfolioId);
  const [definitionId, setDefinitionId] = useState(modelPortfolioId);

  const extraDefinition = {
    date: moment().add(1, 'day').format('DD/MM/YYYY'),
    fund_allocations: [],
    calculated: true
  };

  const [isImporting, setIsImporting] = useState(false);
  const parseDate = (dateStr: string, format = 'DD/MM/YYYY') =>
    moment(dateStr, format);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  const handleDoubleClick = (funds: any) => {
    setDefinitionToEdit({
      ...currentDefinition,
      fund_allocations: funds.map((fa: any) => ({
        ...fa,
        allocation: unFormatPercent(fa.allocation) / 100
      }))
    });
    setIsNewFootprintModalOpen(true);
    setMpDate(parseDate(currentDefinition.date).format('YYYY-MM-DD'));
    setEditableDate(parseDate(currentDefinition.date).format('YYYY-MM-DD'));
  };

  const getAllocationName = (allocation: any) => {
    return allocation.fund?.name || allocation.model_portfolio_selection?.name;
  };

  const round = (num: number) => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  const updateDefinitions = (funds: any) => {
    const updatedDefinitions = [...definitions];
    updatedDefinitions.splice(tab, 1, {
      ...updatedDefinitions[tab],
      fund_allocations: funds.map((fund: any) => ({
        ...fund,
        allocation: unFormatPercent(fund.allocation) / 100
      }))
    });
    setDefinitions(updatedDefinitions);
  };

  const addToModelPortfolio = (imports: any[], type = 'f') => {
    const originalImports = [...imports];
    imports = imports.filter((investment: any) => {
      if ('fund' in investment) {
        investment = investment.fund;
      }
      const { name, id } = investment;

      const isAlreadyAdded = funds.some((addedFund: any) => {
        return (
          (addedFund.fund &&
            addedFund.fund.id === id &&
            addedFund.fund.name === name) ||
          (addedFund.model_portfolio_selection &&
            addedFund.model_portfolio_selection.id === id &&
            addedFund.model_portfolio_selection.name === name)
        );
      });

      return !isAlreadyAdded;
    });

    if (imports.length < originalImports.length) {
      Notify.warn(
        `${type === 'f' ? 'Fund' : 'Model portfolio'} already added`,
        `Duplicated ${type === 'f' ? 'funds' : 'model portfolios'} are not allowed`
      );
    }

    if (imports.length === 1) {
      const newFunds = imports.map(importedFund => {
        const investment = {
          allocation: '0.00%',
          existingWeighting: 0,
          fund: {},
          model_portfolio_selection: {},
          fund_value: '£0.00',
          source_value: 1
        };

        if (type === 'f') {
          investment.fund = importedFund;
        } else {
          investment.model_portfolio_selection = importedFund;
        }

        return investment;
      });
      setFunds([...funds, ...newFunds] as any);

      updateDefinitions([...funds, ...newFunds]);
    } else {
      setFunds([...funds, ...imports.reverse()] as any);
      // updateDefinitions([...funds, ...imports.reverse()]);
    }
  };

  const updateAllocation = (output: any, index: number) => {
    const addedFunds = funds as any;
    addedFunds[index].allocation = output.formatedValue;
    setFunds([...addedFunds] as any);

    updateDefinitions([...addedFunds]);
  };

  const getAllocationTotal = (records: any) => {
    return Number(
      records
        .reduce((total: number, fund: any) => {
          const allocation =
            fund.allocation && typeof fund.allocation === 'string'
              ? fund.allocation.replace('%', '')
              : '0';
          total += Number(allocation);
          return total;
        }, 0)
        .toFixed(2)
    );
  };
  const isOneHundredPercent = () => {
    const validatedDefinitions = definitions
      .filter((definition: any) => !definition.calculated)
      .map((definition: any) => {
        const total = getAllocationTotal(
          definition.fund_allocations.map((fund: any) => ({
            ...fund,
            allocation: `${fund.allocation * 100}%`
          }))
        );

        return {
          ...definition,
          valid: total === 100 || total === 0
        };
      });

    const invalidDefinitions = validatedDefinitions.filter(
      (definition: any) => !definition.valid
    );

    const total = getAllocationTotal(funds);
    setSumTotal(getAllocationTotal(funds));
    if (
      invalidDefinitions.length ||
      (!currentDefinition.calculated && total !== 100 && total !== 0)
    ) {
      setError(
        `Allocations${hasHistory && invalidDefinitions.length ? ` at ${invalidDefinitions[0].date}` : ''} must sum to 100%`
      );
    } else {
      setError('');
    }
  };

  const tabProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      style: {
        borderBottom: '2px solid #eaeaea',
        textTransform: 'none'
      } as CSSProperties
    };
  };

  const sortDefinitions = (list: any) => {
    return list.sort((a: any, b: any) =>
      parseDate(a.date).diff(parseDate(b.date))
    );
  };

  const updateOrCreateDefinition = (definition: any, id: number) => {
    const index = definitions.findIndex((def: any) => def.id === id);

    const updatedDefinitions = [...definitions];
    if (index !== -1) {
      updatedDefinitions[index] = {
        ...updatedDefinitions[index],
        ...definition
      };
    } else {
      updatedDefinitions.splice(updatedDefinitions.length - 1, 0, definition);
    }

    setDefinitions(sortDefinitions(updatedDefinitions));
  };

  const saveOptions = ['Save & Continue', 'Save & Close'];

  const createModelPortfolioSelection = (
    modelPortfolioName: string,
    funds: any,
    date: string,
    history: boolean,
    options: {
      id?: number;
      locked?: boolean;
      individual?: boolean;
      close?: boolean;
      selectionId?: number;
    }
  ) => {
    setIsSubmitting(true);
    if (!hasHistory || options.individual) {
      api
        .createModelPortfolioSelection({
          modelPortfolioId: options.id,
          selectionId: options.selectionId,
          modelPortfolioName,
          funds: funds.map((fund: any) => ({
            ...fund,
            model_portfolio: fund.model_portfolio_selection || {}
          })),
          date,
          history,
          locked: options.locked
        })
        .then(({ data }: any) => {
          const calculatedDefinition =
            data.model_portfolio_definitions.find(
              (definition: any) => definition.calculated
            ) || extraDefinition;
          data.saved = true;
          addModelPortfolioSelection(data);
          setCanClone(true);
          setDefinitionId(data.mp_id);
          const savedDefinitions = [
            ...data.model_portfolio_definitions.filter(
              (definition: any) => !definition.calculated
            ),
            ...(calculatedDefinition.id
              ? [
                  {
                    ...extraDefinition,
                    id: calculatedDefinition.id,
                    date: calculatedDefinition.date,
                    fund_allocations: calculatedDefinition.fund_allocations
                  }
                ]
              : [])
          ];
          setStoredDefinitions(sortDefinitions(savedDefinitions));
          setDefinitions(sortDefinitions(savedDefinitions));
          Notify.success(
            `You have ${
              modelPortfolioId ? 'updated' : 'added'
            }  ${modelPortfolioName} - ${date}`
          );

          setIsAddingFootprint(false);
          setIsNewFootprintModalOpen(false);

          if (options.close) {
            close(true);
          }
        })
        .catch(err => {
          errHelper(err);
          setCanClone(false);
        })
        .finally(() => {
          setIsSubmitting(false);
          setIsCloning(false);
        });
    } else {
      const historicalDefinitions = definitions.filter(
        (definition: any) => !definition.calculated
      );
      const bulkFunds = historicalDefinitions.flatMap((definition: any) =>
        definition.fund_allocations.map((fund: any) => ({
          ...fund,
          date: definition.date,
          allocation: `${fund.allocation * 100}%`
        }))
      );
      api
        .bulkUploadModelPortfolio({
          modelPortfolioId: options.id,
          modelPortfolioName,
          funds: bulkFunds,
          dates: historicalDefinitions.map(
            (definition: any) => definition.date
          ),
          history,
          locked: options.locked,
          selectionId: modelPortfolioId
        })
        .then(({ data }: any) => {
          addModelPortfolioSelection(data);
          setStoredDefinitions(
            sortDefinitions(data.model_portfolio_definitions)
          );
          setDefinitions(sortDefinitions(data.model_portfolio_definitions));
          setIsNewFootprintModalOpen(false);
          setCanClone(true);
          setDefinitionId(data.mp_id);
          Notify.success(
            `You have ${
              modelPortfolioId ? 'updated' : 'added'
            } ${modelPortfolioName}`
          );
          if ((options.close && selectedSaveOptionIndex === 1) || isCloning) {
            close(true);
          }
        })
        .catch(err => {
          errHelper(err);
          setCanClone(false);
        })
        .finally(() => {
          setIsSubmitting(false);
          setIsCloning(false);
        });
    }
  };

  const findPreviousOrNextDefinition = (date: string) => {
    const parsedGivenDate = parseDate(date, 'YYYY-MM-DD');
    let previousDefinition = null;
    let nextDefinition = null;

    const sortedDefinitions = definitions.sort((a: any, b: any) => {
      return parseDate(a.date).isBefore(parseDate(b.date)) ? -1 : 1;
    });

    for (const definition of sortedDefinitions) {
      const parsedDefinitionDate = parseDate(definition.date);

      if (parsedDefinitionDate.isBefore(parsedGivenDate)) {
        previousDefinition = definition;
      } else if (parsedDefinitionDate.isAfter(parsedGivenDate)) {
        nextDefinition = definition;
        break;
      }
    }

    return previousDefinition || nextDefinition;
  };

  const addFootprint = (date: string) => {
    setIsAddingFootprint(true);
    const nearestDefinition = findPreviousOrNextDefinition(date);

    const newDefinitions = definitions;
    setDefinitions(
      sortDefinitions([
        ...(!hasHistory ? [] : newDefinitions),
        {
          date: moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
          fund_allocations: nearestDefinition
            ? nearestDefinition.fund_allocations
            : currentValues.funds,
          history: true
        }
      ])
    );
    setIsAddingFootprint(false);
    setIsNewFootprintModalOpen(false);
  };

  const removeFootprint = ({ id, date }: { id?: number; date?: string }) => {
    const updatedDefinitions = definitions.filter((definition: any) => {
      if (id) {
        return definition.id !== id;
      }
      if (date) {
        return definition.date !== date;
      }
      return true;
    });

    setDefinitions(updatedDefinitions);
  };

  const deleteFootprint = () => {
    setIsDeleting(true);
    const editableDefinitions = definitions.filter(
      (definition: any) => !definition.calculated
    );

    if (editableDefinitions.length === 1) {
      const removeHistoryRecord = () => {
        if (!definitionToDelete.id) {
          updateOrCreateDefinition(
            { ...definitionToDelete, history: false },
            definitionToDelete.id
          );
        } else {
          setNameToSave(currentValues.modelPortfolioName);
          createModelPortfolioSelection(
            nameToSave,
            currentValues.funds,
            parseDate(definitionToDelete.date).format('YYYY-MM-DD'),
            false,
            {
              id: definitionToDelete.id,
              locked: currentValues.locked,
              individual: true
            }
          );
        }
      };

      const calculatedDefinition = definitions.find(
        (definition: any) => definition.calculated
      );

      if (calculatedDefinition && calculatedDefinition.id) {
        api
          .deleteModelPortfolioDefinition(calculatedDefinition.id as number)
          .then(() => {
            removeHistoryRecord();
          })
          .catch(err => {
            errHelper(err);
          })
          .finally(() => {
            setIsDeleting(false);
            setIsDeletePromptOpen(false);
          });
      } else {
        removeHistoryRecord();
        setIsDeleting(false);
        setIsDeletePromptOpen(false);
      }

      return;
    }

    if (!definitionToDelete.id) {
      removeFootprint(definitionToDelete);
      setIsDeletePromptOpen(false);
      setIsDeleting(false);
      return;
    }

    api
      .deleteModelPortfolioDefinition(definitionToDelete.id as number)
      .then(() => {
        removeFootprint(definitionToDelete);
        Notify.success(
          `You have deleted ${modelPortfolioName} - ${definitionToDelete.date}`
        );
      })
      .catch(err => {
        errHelper(err);
      })
      .finally(() => {
        setIsDeleting(false);
        setIsDeletePromptOpen(false);
      });
  };

  const getBulkUploadData = async (results: any) => {
    const headers = results.data[0].map((header: string) => {
      const trimmedHeader = header.trim().toLowerCase();
      if (trimmedHeader.includes('date')) {
        return 'date';
      } else if (trimmedHeader.includes('isin')) {
        return 'isin';
      } else if (trimmedHeader.includes('weight')) {
        return 'weight';
      } else {
        return null;
      }
    });

    if (!headers.filter(Boolean).length) {
      Notify.error('Please ensure the CSV is in the correct format.');
      return null;
    }

    const result = results.data
      .slice(1)
      .filter((row: any) => row.length === headers.length)
      .map((row: any) => {
        const obj = {} as any;
        row.forEach((value: any, index: number) => {
          obj[headers[index]] = value.trim();
        });
        obj.moment = moment(obj.date, 'DD/MM/YYYY');
        obj.dateValid = obj.moment.format('DD/MM/YYYY') === obj.date;
        return obj;
      })
      .filter((row: any) => row.dateValid)
      .sort((a: any, b: any) => {
        const dateA = a.moment as any;
        const dateB = b.moment as any;
        return dateA - dateB;
      });

    if (!result.length) {
      Notify.error(
        'Please ensure the dates are formatted correctly (DD/MM/YYYY).'
      );
      return null;
    }

    const response: any = await api
      .bulkImportFunds(result.map((item: any) => item.isin))
      .catch(() => {
        Notify.error('Something went wrong');
      });

    const missingIsins: string[] = [];

    const finalResult = result.map((item: any) => {
      const match = response.data.find((fund: any) => fund.isin === item.isin);

      if (
        !match &&
        item.isin.toLowerCase() !== 'cash' &&
        !missingIsins.includes(item.isin)
      ) {
        missingIsins.push(item.isin);
      }

      return {
        ...item,
        exists: !!match,
        name: match?.name,
        id: match?.id,
        active: match?.active
      };
    });

    setMissingIsins(missingIsins);

    return finalResult;
  };

  const equaliseAllocations = (funds: any) => {
    const value = formatPercent(100 / funds.length);
    funds.forEach((fund: any, index: number) => {
      updateAllocation({ formatedValue: value }, index);
    });

    const total = funds
      .map((fund: any) => Number(unFormatPercent(fund.allocation)))
      .reduce(
        (accumulator: any, currentValue: any) => accumulator + currentValue,
        0
      );

    if (total !== 100) {
      updateAllocation(
        {
          formatedValue: formatPercent(
            Number(unFormatPercent(funds[0].allocation)) + (100 - total)
          )
        },
        0
      );
    }
  };

  useEffect(() => {
    isOneHundredPercent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funds]);

  useEffect(() => {
    setModelPortfolioName(defaults?.name || '');
    setFunds(defaults?.funds || []);
  }, [defaults]);

  useEffect(() => {
    setNameToSave(modelPortfolioName);
  }, [modelPortfolioName]);

  useEffect(() => {
    if (modelPortfolioId) {
      setIsLoading(true);
      api
        .getModelPortfolioSelection({ id: modelPortfolioId })
        .then(({ data }: AxiosResponse) => {
          const calculatedDefinition =
            data.model_portfolio_definitions.find(
              (definition: any) => definition.calculated
            ) || extraDefinition;
          setModelPortfolioName(data.name);
          setLocked(data.locked_user_id);
          setLockedUser(data.locked_user);
          setLockedByAnotherUser(
            data.locked_user_id !== null &&
              user !== null &&
              data.locked_user_id !== user['id']
          );
          const fetchedDefinitions = [
            ...data.model_portfolio_definitions.filter(
              (definition: any) => !definition.calculated
            ),
            ...(calculatedDefinition.id
              ? [
                  {
                    ...extraDefinition,
                    id: calculatedDefinition.id,
                    date: calculatedDefinition.date,
                    fund_allocations: calculatedDefinition.fund_allocations
                  }
                ]
              : [])
          ];
          setStoredDefinitions(sortDefinitions(fetchedDefinitions));
          setDefinitions(sortDefinitions(fetchedDefinitions));
          setIsLoading(false);
        });
      return;
    }

    setIsLoading(false);
    setDefinitions([
      {
        date: moment().format('DD/MM/YYYY'),
        fund_allocations: [],
        history: false
      }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentDefinition(definitions[tab] || emptyDefinition);
    setHasHistory(definitions.some((definition: any) => definition.history));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions, tab]);

  useEffect(() => {
    setFunds(
      (currentDefinition?.fund_allocations || []).map((fa: any) => ({
        ...fa,
        allocation: `${round(fa.allocation * 100)}%`
      }))
    );
  }, [currentDefinition]);

  useEffect(() => {
    setHasChanges(!isEqual(storedDefinitions, definitions));
  }, [storedDefinitions, definitions]);

  const generateUniqueCloneName = (
    baseName: string,
    portfolios: { name: string }[]
  ) => {
    const cleanBaseName = baseName.replace(/ - Cloned( \d+)?$/, '');
    const cloneBase = `${cleanBaseName} - Cloned`;

    const cloneNames = portfolios
      .map(portfolio => portfolio.name)
      .filter(name => name.startsWith(cloneBase));

    const cloneNumbers = cloneNames.map((name: string) => {
      const parts = name.split(' ');
      const lastPart = parts[parts.length - 1];

      const number = parseInt(lastPart, 10);
      return !isNaN(number) ? number : 0;
    });

    const nextCloneNumber =
      cloneNumbers.length > 0 ? Math.max(...cloneNumbers) + 1 : 0;

    return nextCloneNumber > 0 ? `${cloneBase} ${nextCloneNumber}` : cloneBase;
  };

  const mergeBulkImportedFunds = (items: any) => {
    const merged = {} as any;
    const duplicates: string[] = [];

    items.forEach((item: any) => {
      const isin = item.isin;
      const weightValue = Number(unFormatPercent(item.weight));

      if (!merged[isin]) {
        merged[isin] = { ...item, weight: weightValue };
      } else {
        if (!duplicates.includes(isin)) {
          duplicates.push(isin);
        }
        merged[isin].weight += weightValue;
      }
    });

    if (duplicates.length) {
      Notify.info(
        `Duplicate records have been found and merged for ISINs: ${duplicates.join(', ')} in ${items[0].date}`
      );
    }

    // Convert the merged object back to an array
    return Object.values(merged).map((item: any) => ({
      ...item,
      weight: formatPercent(item.weight)
    }));
  };

  return (
    <>
      {(isLoading || isSubmitting || isCloning) && (
        <div className={classes.progressBox}>
          <div className={classes.progressSpinner}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '1rem'
              }}
            >
              <CircularProgress />
              Loading...
            </div>
          </div>
        </div>
      )}
      <Formik
        enableReinitialize
        initialValues={{
          funds: funds,
          modelPortfolioName: modelPortfolioName,
          date: mpDate,
          locked: locked
        }}
        validationSchema={modelPortfolioValidationSchema}
        onSubmit={values => {
          setNameToSave(values.modelPortfolioName);
          const method = onCreate ? onCreate : createModelPortfolioSelection;
          method(
            nameToSave,
            values.funds,
            parseDate(currentDefinition.date).format('YYYY-MM-DD'),
            currentDefinition.history,
            {
              id: isCloning ? null : currentDefinition.id,
              locked: values.locked,
              close: isCloning || selectedSaveOptionIndex === 1
            }
          );
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          submitForm
        }: FormikProps<any>) => {
          const removeInvestment = (index: number) => {
            values.funds.splice(index, 1);
            setFieldValue('funds', values.funds);
            setFunds([...values.funds] as any);
            updateDefinitions([...values.funds]);
          };
          return (
            <form onSubmit={handleSubmit} style={{ overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex',
                  padding: '0.75rem',
                  maxWidth: '90vw',
                  height: '870px'
                }}
              >
                <div style={{ padding: '0 1rem 0 0.25rem', width: '400px' }}>
                  <div>
                    <div>
                      <div>
                        <Field
                          label="Model Portfolio Name"
                          disabled={lockedByAnotherUser}
                          name="modelPortfolioName"
                          component={Fields.v2.Text}
                          InputProps={{
                            onInput: (e: any) => {
                              setModelPortfolioName(e.target.value);
                            }
                          }}
                        />
                      </div>
                      {canLock && (
                        <Tooltip
                          arrow
                          title={
                            lockedByAnotherUser
                              ? `Locked by: ${lockedUser.first_name} ${lockedUser.last_name}`
                              : ''
                          }
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                disabled={lockedByAnotherUser}
                                value={locked}
                                checked={locked}
                                name="locked"
                                onChange={e => {
                                  setLocked(e.target.checked);
                                }}
                              />
                            }
                            label={
                              <Typography variant="subtitle2">
                                Lock edit to user
                              </Typography>
                            }
                            style={{
                              flexDirection: 'row-reverse',
                              marginLeft: '1px'
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {!lockedByAnotherUser && (
                    <ModelPortfolioAllocationOptions
                      addInvestment={addToModelPortfolio}
                      removeInvestment={removeInvestment}
                      setIsModalHidden={setIsModalHidden}
                      funds={values.funds}
                      hideCashFund={false}
                    />
                  )}
                </div>
                <div
                  style={{
                    flex: 'auto',
                    maxWidth: '70%',
                    borderLeft: '1px solid #d4d4d4'
                  }}
                >
                  {isLoading ? (
                    <LoaderSpinner isLoading={isLoading} />
                  ) : (
                    <>
                      {children}
                      {canHaveHistory && (
                        <>
                          {hasHistory ? (
                            <Tabs
                              style={{
                                margin: '1rem 0',
                                padding: '0 1rem',
                                borderBottom: '1px solid #d4d4d4'
                              }}
                              value={tab}
                              onChange={handleTabChange}
                              TabIndicatorProps={{
                                style: { display: 'none' }
                              }}
                              variant="scrollable"
                            >
                              {definitions.map(
                                (definition: any, index: number) => {
                                  const label = definition.calculated
                                    ? 'Current'
                                    : definition.date;
                                  const isCurrent =
                                    hasHistory && definition.calculated;
                                  const isActive =
                                    currentDefinition.date === definition.date;
                                  const canDelete = !isCurrent;

                                  return (
                                    <Tooltip
                                      key={index}
                                      title={
                                        isCurrent
                                          ? ''
                                          : 'Double click to update date.'
                                      }
                                      arrow
                                      enterDelay={500}
                                      enterNextDelay={500}
                                    >
                                      <Tab
                                        onDoubleClick={() => {
                                          if (isCurrent) {
                                            return;
                                          }
                                          handleDoubleClick(values.funds);
                                        }}
                                        {...tabProps(index)}
                                        label={
                                          <div
                                            style={{
                                              width: '100%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent:
                                                isCurrent || !canDelete
                                                  ? 'center'
                                                  : 'space-between'
                                            }}
                                          >
                                            {canDelete && <span></span>}
                                            <span>{label}</span>
                                            {canDelete && (
                                              <IconButton
                                                size="small"
                                                component="span"
                                                style={{
                                                  color: isActive
                                                    ? 'rgba(255, 255, 255, 0.54)'
                                                    : 'rgba(0, 0, 0, 0.54)'
                                                }}
                                                onClick={() => {
                                                  setDefinitionToDelete(
                                                    definition
                                                  );
                                                  setCurrentValues(values);
                                                  setIsDeletePromptOpen(true);
                                                }}
                                              >
                                                <CloseIcon />
                                              </IconButton>
                                            )}
                                          </div>
                                        }
                                      />
                                    </Tooltip>
                                  );
                                }
                              )}
                              {!lockedByAnotherUser && (
                                <div
                                  style={{
                                    padding: '0 1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    setIsNewFootprintModalOpen(true);
                                    setCurrentValues(values);
                                  }}
                                >
                                  <AddCircleIcon color="secondary" />
                                </div>
                              )}
                            </Tabs>
                          ) : (
                            <>
                              {!lockedByAnotherUser && (
                                <div
                                  style={{
                                    display: 'flex',
                                    margin: '0.5rem 1rem'
                                  }}
                                >
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      setIsNewFootprintModalOpen(true);
                                      setCurrentValues(values);
                                    }}
                                  >
                                    Add Portfolio History
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      <div
                        style={{
                          height: '85vh',
                          overflowY: 'auto',
                          borderBottomRightRadius: '25px',
                          borderBottomLeftRadius: '25px',
                          paddingBottom: '8rem'
                        }}
                      >
                        {currentDefinition.calculated && !!hasChanges && (
                          <Warning
                            text={
                              'You must save the portfolio for the current allocation to update.'
                            }
                          />
                        )}
                        <div className={classes.titleRow}>
                          <div style={{ width: '200px' }}>Fund</div>
                          <div style={{ width: '120px' }}>ISIN</div>
                          <div style={{ width: '120px' }}>
                            Start of data date
                          </div>
                          <div
                            style={{
                              width: '70px',
                              marginLeft: '0.5rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            Allocation
                            {values.funds.length > 0 &&
                              !currentDefinition.calculated && (
                                <Tooltip
                                  arrow
                                  title="Apply equal allocations across all funds"
                                >
                                  <DragHandleIcon
                                    style={{
                                      color: linkColor,
                                      cursor: 'pointer'
                                    }}
                                    onClick={() =>
                                      equaliseAllocations(values.funds)
                                    }
                                  />
                                </Tooltip>
                              )}
                          </div>
                          <div>Delete</div>
                        </div>
                        <>
                          {values.funds
                            .sort((a: any, b: any) => {
                              const aName = getAllocationName(a).toLowerCase();
                              const bName = getAllocationName(b).toLowerCase();

                              if (aName < bName) {
                                return -1;
                              }
                              if (aName > bName) {
                                return 1;
                              }
                              return 0;
                            })
                            .map((fund: any, index: number) => {
                              const isCurrent =
                                hasHistory && currentDefinition.calculated;

                              const isModelPortfolio =
                                !!fund.model_portfolio_definition ||
                                !!fund.model_portfolio_selection;

                              const hasMPInactiveFund = isModelPortfolio
                                ? (
                                    fund.model_portfolio_definition
                                      ?.fund_allocations || []
                                  ).some((fundDetails: any) => {
                                    return (
                                      fundDetails.fund &&
                                      !fundDetails.fund.active
                                    );
                                  })
                                : false;

                              const active = isModelPortfolio
                                ? true
                                : fund.fund.active;
                              const isFundActive = isModelPortfolio
                                ? !hasMPInactiveFund
                                : active;

                              const isFundValid = isModelPortfolio
                                ? true
                                : typeof fund.fund.isValid === 'boolean'
                                  ? fund.fund.isValid
                                  : true;
                              return (
                                <div
                                  key={getAllocationName(fund)}
                                  className={classes.mpFundRow}
                                  style={{
                                    backgroundColor:
                                      index % 2 === 0 ? '#f9f9f9' : 'white'
                                  }}
                                >
                                  <div style={{ width: '200px' }}>
                                    <div style={{ display: 'flex' }}>
                                      {getAllocationName(fund)}
                                      {(!isFundActive || !isFundValid) && (
                                        <TooltipIcon
                                          text={`${
                                            !isFundValid
                                              ? 'Fund is invalid'
                                              : 'Fund is no longer supported'
                                          } and will not be accounted for when calculating the current holdings`}
                                          icon="warn"
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ width: '120px' }}>
                                    {fund?.fund?.isin || '-'}
                                  </div>
                                  <div style={{ width: '120px' }}>
                                    {fund?.fund?.performance_inception_date ||
                                      '-'}
                                  </div>
                                  {!isCurrent ? (
                                    <>
                                      <Field
                                        name={`funds[${index}].allocation`}
                                        placeholder="0.00%"
                                        component={Fields.v2.Percent}
                                        style={{ width: '75px' }}
                                        onChange={(e: any) => {
                                          updateAllocation(e, index);
                                        }}
                                        disabled={
                                          isCurrent || lockedByAnotherUser
                                        }
                                      />
                                      {!lockedByAnotherUser && (
                                        <Icons.CloseIcon
                                          style={{ height: '24px' }}
                                          onClick={() =>
                                            removeInvestment(index)
                                          }
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <div>{fund.allocation}</div>
                                      <div></div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                        </>

                        {sumTotal !== 0 && (
                          <div
                            key={'total'}
                            className={classes.mpFundRow}
                            style={{
                              backgroundColor:
                                funds.length % 2 === 0 ? '#f9f9f9' : 'white'
                            }}
                          >
                            <div className={classes.total}>
                              <div style={{ width: '55%' }}>TOTAL</div>
                              <div
                                style={{
                                  width: '30%',
                                  display: 'flex',
                                  justifyContent: 'flex-end'
                                }}
                              >{`${sumTotal}%`}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className={classes.errorContainer}>
                {error.length !== 0 && (
                  <div className={classes.errorBox}>
                    <InfoIcon className={classes.icon} />
                    <p className={classes.errorText}>{error}</p>
                  </div>
                )}
              </div>
              <div className={classes.buttonContainer}>
                <Button
                  color="primary"
                  onClick={() => {
                    close();
                  }}
                >
                  Cancel
                </Button>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  {canClone && !lockedByAnotherUser && (
                    <Button
                      type="button"
                      onClick={() => {
                        if (onClone) {
                          onClone();
                        }
                        setNameToSave(
                          generateUniqueCloneName(
                            values.modelPortfolioName,
                            investments
                          )
                        );
                        setIsCloning(true);
                        setTimeout(() => {
                          submitForm();
                        }, 100);
                      }}
                    >
                      Clone
                    </Button>
                  )}
                  <ButtonDropdown
                    disabled={
                      isSubmitting || error !== '' || lockedByAnotherUser
                    }
                    options={saveOptions}
                    setOption={setselectedSaveOptionIndex}
                    defaultOption={1}
                  />
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
      <Modal
        isOpen={isNewFootprintModalOpen}
        closeModalFunc={() => setIsNewFootprintModalOpen(false)}
      >
        <BoxWrapper
          title="Add Portfolio History"
          closeModal={() => setIsNewFootprintModalOpen(false)}
          style={{ maxWidth: '500px' }}
        >
          <Formik
            enableReinitialize
            initialValues={{
              date: mpDate,
              type: 'manual'
            }}
            validateOnChange
            validationSchema={footprintValidationSchema}
            onSubmit={() => {
              let editedDefinition = definitionToEdit;
              if (!hasHistory) {
                editedDefinition = definitions[0];
              }

              if (editedDefinition) {
                const newDefinition = {
                  ...editedDefinition,
                  history: true,
                  date: parseDate(editableDate, 'YYYY-MM-DD').format(
                    'DD/MM/YYYY'
                  )
                };

                updateOrCreateDefinition(newDefinition, editedDefinition.id);
                setIsNewFootprintModalOpen(false);
              } else {
                addFootprint(editableDate);
              }

              setDefinitionToEdit(null);
              setMpDate(defaultDate);
            }}
          >
            {({
              values,
              handleSubmit,
              setFieldValue,
              errors
            }: FormikProps<any>) => {
              return (
                <form onSubmit={handleSubmit}>
                  <div style={{ margin: '1rem', paddingBottom: '3rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Checkbox
                        onClick={() => setFieldValue('type', 'manual')}
                        label={'Manual'}
                        isChecked={values.type === 'manual'}
                      />
                      <Checkbox
                        onClick={() => setFieldValue('type', 'bulk')}
                        label={'Bulk upload'}
                        isChecked={values.type === 'bulk'}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: '1rem',
                        maxHeight: '70vh',
                        overflow: 'auto'
                      }}
                    >
                      {values.type === 'manual' ? (
                        <div
                          style={{ width: '300px', marginBottom: '2rem' }}
                          className={classes.nameAndDate}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <label>Start date or rebalance date</label>
                            <TextField
                              type="date"
                              value={editableDate}
                              onChange={e => setEditableDate(e.target.value)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <CSVReader
                            onUploadAccepted={async (results: any) => {
                              try {
                                setIsImporting(true);

                                const funds = await getBulkUploadData(results);

                                if (funds) {
                                  const groupedFunds = groupBy(
                                    funds.filter((fund: any) => fund.exists),
                                    'date'
                                  );
                                  const newDefinitions = Object.keys(
                                    groupedFunds
                                  ).map((key: string) => {
                                    return {
                                      id: definitionId,
                                      history: true,
                                      date: key,
                                      fund_allocations: mergeBulkImportedFunds(
                                        groupedFunds[key]
                                      ).map((fund: any) => ({
                                        allocation:
                                          unFormatPercent(fund.weight) / 100,
                                        fund: {
                                          active: fund.active,
                                          id: fund.id,
                                          isin: fund.isin,
                                          name: fund.name
                                        }
                                      }))
                                    };
                                  });

                                  setDefinitions(
                                    sortDefinitions(newDefinitions)
                                  );
                                  setIsNewFootprintModalOpen(
                                    !missingIsins.length
                                  );
                                }
                              } finally {
                                setIsImporting(false);
                              }
                            }}
                            noDrag
                          >
                            {({ getRootProps }: any) => (
                              <Button
                                isLoading={isImporting}
                                {...getRootProps()}
                                style={{ marginBottom: '1rem' }}
                                type="button"
                              >
                                Select file
                              </Button>
                            )}
                          </CSVReader>
                          <div className={classes.mutedText}>
                            Please upload a CSV file in the correct format.{' '}
                            <CSVDownloader
                              filename={'Portfolio History Template'}
                              data={
                                'As at date (DD/MM/YYYY),Fund ISIN code,Weight (%)\n17/05/2024,GB00B4KWNF91,2.50%'
                              }
                              bom={true}
                              download={true}
                            >
                              <p title="Download CSV template">
                                Download template
                              </p>
                            </CSVDownloader>
                            <br />
                            This will overwrite any existing portfolio history.
                          </div>
                          <div className={classes.instructions}>
                            <p>CSV Format Guidelines:</p>
                            <ul>
                              <li>First column: As at date (DD/MM/YYYY)</li>
                              <li>Second column: Fund ISIN code</li>
                              <li>Third column: Weight (%)</li>
                            </ul>
                          </div>
                          {missingIsins.length > 0 && (
                            <Alert severity="info">
                              {`The following ISINs could not be found and will not be imported as they do not exist: ${missingIsins.join(
                                ', '
                              )}. As a result, allocations may not sum to 100%.`}
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={classes.buttonContainer}>
                    <Button
                      color="primary"
                      onClick={() => {
                        setIsNewFootprintModalOpen(false);
                        setMpDate(defaultDate);
                      }}
                    >
                      Cancel
                    </Button>
                    {values.type === 'manual' ? (
                      <Button
                        type="submit"
                        disabled={
                          Object.keys(errors).length > 0 || isAddingFootprint
                        }
                        isLoading={isAddingFootprint}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        disabled={isImporting || missingIsins.length === 0}
                        onClick={() => {
                          setIsNewFootprintModalOpen(false);
                        }}
                      >
                        Continue
                      </Button>
                    )}
                  </div>
                </form>
              );
            }}
          </Formik>
        </BoxWrapper>
      </Modal>
      <Prompt
        isOpen={isDeletePromptOpen}
        title={`Are you sure you want to delete this history?`}
        confirmAction={() => deleteFootprint()}
        isProcessing={isDeleting}
        cancelAction={() => setIsDeletePromptOpen(false)}
      />
    </>
  );
};

export default ModelPortfolioCreator;
